import { useState } from 'react'
import {
  Button, Form, Input, InputNumber, Select, Tag, Space, App, Divider,
  Row, Col, Statistic, Card, Typography, Badge, Drawer, Alert,
} from 'antd'
import {
  PlusOutlined, EditOutlined, CarOutlined, CheckCircleOutlined,
  CloseCircleOutlined, EyeOutlined, AuditOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gateEntriesApi, suppliersApi, shiftsApi } from '@/features/gate/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { GateEntry } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import {
  weighbridgeCorrectionsApi,
  type CorrectionType,
} from '@/features/gate/weighbridgeCorrectionsApi'

const { Text } = Typography

const ENTRY_TYPES = ['RAW_MATERIAL', 'SPARES', 'CONSUMABLES', 'PACKING', 'OTHERS']
const STATUSES = ['ENTERED', 'AT_WEIGHBRIDGE_GROSS', 'GROSS_WEIGHED', 'UNLOADING',
  'AT_WEIGHBRIDGE_TARE', 'TARE_WEIGHED', 'COMPLETED', 'VOIDED']

const statusColor: Record<string, string> = {
  ENTERED: 'processing', AT_WEIGHBRIDGE_GROSS: 'warning', GROSS_WEIGHED: 'warning',
  UNLOADING: 'gold', AT_WEIGHBRIDGE_TARE: 'warning', TARE_WEIGHED: 'cyan',
  COMPLETED: 'success', VOIDED: 'error',
}

export default function GateEntryPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<GateEntry | null>(null)
  const [viewing, setViewing] = useState<GateEntry | null>(null)

  const canCreate = usePermission('gate.create')
  const canUpdate = usePermission('gate.update')
  const canRaiseCorrection = usePermission('weighbridge.correction.raise')

  // Sprint 0147 — Raise Correction drawer state
  const [corrOpen, setCorrOpen] = useState(false)
  const [corrForm] = Form.useForm()
  const raiseCorrMut = useMutation({
    mutationFn: weighbridgeCorrectionsApi.raise,
    onSuccess: (d) => {
      message.success(
        `Correction ${d.correction_no} raised — Accountant queue. CFO + MD notified.`,
      )
      setCorrOpen(false)
      corrForm.resetFields()
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } }
      message.error(e?.response?.data?.detail ?? 'Failed to raise correction')
    },
  })

  // Existing corrections for the entry currently being viewed.
  const viewingCorrQ = useQuery({
    enabled: !!viewing && viewDrawerOpen,
    queryKey: ['wb-corrs-for-entry', viewing?.id],
    queryFn: () => viewing
      ? weighbridgeCorrectionsApi.list({ gate_entry_id: viewing.id })
      : Promise.resolve([]),
  })

  // For plant dropdown (use first plant from user or pick from list)
  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 200 }),
  })
  const plantOptions = plantsData?.items?.map((p: { id: string; name: string }) => ({ value: p.id, label: p.name })) ?? []
  const firstPlantId = plantOptions[0]?.value

  const { data: todaySummary } = useQuery({
    queryKey: ['gate-today', firstPlantId],
    queryFn: () => firstPlantId ? gateEntriesApi.todaySummary(firstPlantId) : null,
    enabled: !!firstPlantId,
  })

  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ size: 200 }),
  })
  const supplierOptions = suppliersData?.items?.map((s: { id: string; supplier_name: string }) => ({
    value: s.id, label: s.supplier_name,
  })) ?? []

  const { data, isLoading } = useQuery({
    queryKey: ['gate-entries', page, pageSize, search, statusFilter],
    queryFn: () => gateEntriesApi.list({ page, size: pageSize, search: search || undefined, status: statusFilter }),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ entry_type: 'RAW_MATERIAL', plant_id: firstPlantId })
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? gateEntriesApi.update(editing.id, values) : gateEntriesApi.create({ ...values, items: [] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gate-entries'] })
      qc.invalidateQueries({ queryKey: ['gate-today'] })
      message.success(editing ? 'Entry updated' : 'Gate entry created')
      setDrawerOpen(false)
    },
    onError: (e: Error) => message.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed'),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      gateEntriesApi.updateStatus(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gate-entries'] })
      qc.invalidateQueries({ queryKey: ['gate-today'] })
      message.success('Status updated')
    },
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Inward No', dataIndex: 'inward_no', key: 'no', width: 130,
      render: (v: string) => <Text strong>{v}</Text> },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 170,
      render: (v: string) => <Badge status={statusColor[v] as never} text={v.replace(/_/g, ' ')} />,
    },
    { title: 'Type', dataIndex: 'entry_type', key: 'type',
      render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'sup', render: (v?: string) => v ?? '—' },
    { title: 'Vehicle', dataIndex: 'vehicle_number', key: 'vehicle' },
    { title: 'PO No.', dataIndex: 'po_number', key: 'po', render: (v?: string) => v ?? '—' },
    { title: 'Entry Time', dataIndex: 'entry_time', key: 'time',
      render: (v: string) => new Date(v).toLocaleString('en-IN') },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: GateEntry) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => { setViewing(record); setViewDrawerOpen(true) }} />
          {canUpdate && record.status === 'ENTERED' && (
            <Button size="small" type="primary"
              onClick={() => statusMutation.mutate({ id: record.id, status: 'AT_WEIGHBRIDGE_GROSS' })}>
              → Weighbridge
            </Button>
          )}
          {canUpdate && record.status === 'GROSS_WEIGHED' && (
            <Button size="small" onClick={() => statusMutation.mutate({ id: record.id, status: 'UNLOADING' })}>
              → Unloading
            </Button>
          )}
          {canUpdate && record.status === 'UNLOADING' && (
            <Button size="small" onClick={() => statusMutation.mutate({ id: record.id, status: 'AT_WEIGHBRIDGE_TARE' })}>
              → Tare
            </Button>
          )}
          {canUpdate && record.status === 'TARE_WEIGHED' && (
            <Button size="small" type="primary" style={{ background: '#52c41a' }}
              onClick={() => statusMutation.mutate({ id: record.id, status: 'COMPLETED' })}>
              Complete
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Gate Entry" subtitle="Truck inward management — today's gate dashboard"
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            <CarOutlined /> New Truck Entry
          </Button>
        )}
      />

      {/* Summary cards */}
      {todaySummary && (
        <Row gutter={12} style={{ marginBottom: 16 }}>
          {[
            { label: "Today's Total", key: 'total', color: '#1890ff' },
            { label: 'Inside', key: 'entered', color: '#faad14' },
            { label: 'At Weighbridge', key: 'at_weighbridge', color: '#722ed1' },
            { label: 'Unloading', key: 'unloading', color: '#fa8c16' },
            { label: 'Completed', key: 'completed', color: '#52c41a' },
            { label: 'Voided', key: 'voided', color: '#f5222d' },
          ].map(({ label, key, color }) => (
            <Col span={4} key={key}>
              <Card size="small" style={{ textAlign: 'center', borderTop: `3px solid ${color}` }}>
                <Statistic title={label} value={(todaySummary as Record<string, number>)[key] ?? 0}
                  valueStyle={{ color }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Space style={{ marginBottom: 16 }}>
        <Input.Search placeholder="Inward no, vehicle, PO…" allowClear
          onSearch={(v) => { setSearch(v); setPage(1) }} style={{ width: 260 }} />
        <Select placeholder="Status" allowClear style={{ width: 180 }}
          options={STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') }))}
          onChange={(v) => { setStatusFilter(v); setPage(1) }} />
      </Space>

      <DataTable rowKey="id" columns={columns} dataSource={data?.items} loading={isLoading}
        total={data?.total} page={page} pageSize={pageSize} onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }} />

      {/* Create drawer */}
      <FormDrawer title="New Gate Entry" open={drawerOpen}
        onClose={() => setDrawerOpen(false)} onSubmit={onSubmit}
        loading={saveMutation.isPending} form={form}>
        <Divider orientation="left" plain>Entry Details</Divider>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="entry_type" label="Entry Type" rules={[{ required: true }]}>
          <Select options={ENTRY_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))} />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier">
          <Select options={supplierOptions} showSearch allowClear
            filterOption={(input, opt) => (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="po_number" label="PO Number">
          <Input />
        </Form.Item>
        <Form.Item name="invoice_number" label="Invoice Number">
          <Input />
        </Form.Item>
        <Form.Item name="invoice_weight_kg" label="Invoice Weight (kg)">
          <Input type="number" />
        </Form.Item>

        <Divider orientation="left" plain>Vehicle & Driver</Divider>
        <Form.Item name="vehicle_number" label="Vehicle Number" rules={[{ required: true }]}>
          <Input placeholder="MH-12-AB-1234" />
        </Form.Item>
        <Form.Item name="vehicle_type" label="Vehicle Type">
          <Select allowClear options={['TRUCK', 'MINI_TRUCK', 'TEMPO', 'CONTAINER', 'OTHER']
            .map(v => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item name="driver_name" label="Driver Name">
          <Input />
        </Form.Item>
        <Form.Item name="driver_mobile" label="Driver Mobile">
          <Input />
        </Form.Item>
        <Form.Item name="driver_license_no" label="License No">
          <Input />
        </Form.Item>
        <Form.Item name="guard_remarks" label="Guard Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </FormDrawer>

      {/* View drawer */}
      <FormDrawer title={`Entry: ${viewing?.inward_no}`} open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)} hideSubmit form={form}>
        {viewing && (
          <div>
            <Row gutter={16}>
              <Col span={12}><Text type="secondary">Status</Text><br />
                <Badge status={statusColor[viewing.status] as never} text={viewing.status.replace(/_/g, ' ')} /></Col>
              <Col span={12}><Text type="secondary">Entry Type</Text><br /><Text>{viewing.entry_type}</Text></Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span={12}><Text type="secondary">Supplier</Text><br /><Text>{viewing.supplier_name || '—'}</Text></Col>
              <Col span={12}><Text type="secondary">PO No</Text><br /><Text>{viewing.po_number || '—'}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}><Text type="secondary">Vehicle</Text><br /><Text>{viewing.vehicle_number}</Text></Col>
              <Col span={12}><Text type="secondary">Driver</Text><br /><Text>{viewing.driver_name || '—'}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}><Text type="secondary">Invoice No</Text><br /><Text>{viewing.invoice_number || '—'}</Text></Col>
              <Col span={12}><Text type="secondary">Invoice Weight</Text><br />
                <Text>{viewing.invoice_weight_kg ? `${viewing.invoice_weight_kg} kg` : '—'}</Text></Col>
            </Row>

            {/* ─── Sprint 0147 — Weighbridge corrections ─── */}
            <Divider />
            <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
              <Col>
                <Text strong>Weighbridge corrections</Text>
                {viewingCorrQ.data?.length ? (
                  <Tag style={{ marginLeft: 6 }} color="orange">
                    {viewingCorrQ.data.length}
                  </Tag>
                ) : null}
              </Col>
              <Col>
                {canRaiseCorrection && (
                  <Button
                    type="primary" ghost size="small"
                    icon={<AuditOutlined />}
                    onClick={() => { corrForm.resetFields(); setCorrOpen(true) }}
                  >
                    Raise correction
                  </Button>
                )}
              </Col>
            </Row>
            {viewingCorrQ.data?.length ? (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {viewingCorrQ.data.map((c) => (
                  <Card key={c.id} size="small" bodyStyle={{ padding: '6px 10px' }}>
                    <Row justify="space-between">
                      <Col>
                        <Text strong>{c.correction_no}</Text>
                        <Tag color="blue" style={{ marginLeft: 6 }}>
                          {c.correction_type}
                        </Tag>
                        <Tag color={c.status === 'PENDING' ? 'orange' : c.status === 'APPROVED' ? 'green' : c.status === 'REJECTED' ? 'red' : 'default'}>
                          {c.status}
                        </Tag>
                      </Col>
                      <Col>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {c.original_net_kg ?? '—'} kg → {c.corrected_net_kg ?? '—'} kg
                        </Text>
                      </Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {c.reason}
                    </Text>
                  </Card>
                ))}
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                No corrections raised on this entry.
              </Text>
            )}
          </div>
        )}
      </FormDrawer>

      {/* Sprint 0147 — Raise Correction drawer */}
      <Drawer
        title={viewing ? `Raise correction — ${viewing.inward_no}` : 'Raise correction'}
        width={520}
        open={corrOpen}
        onClose={() => setCorrOpen(false)}
        extra={
          <Button
            type="primary"
            loading={raiseCorrMut.isPending}
            onClick={() => corrForm.submit()}
          >
            Submit to Accountant
          </Button>
        }
      >
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message="Routes to Accountant for approval. CFO + MD are notified."
          description="Fill ONLY the fields that need correcting. Leave the rest blank — the original values stay."
        />
        <Form
          form={corrForm}
          layout="vertical"
          initialValues={{ correction_type: 'GROSS' }}
          onFinish={(v) => {
            if (!viewing) return
            raiseCorrMut.mutate({
              gate_entry_id: viewing.id,
              correction_type: v.correction_type as CorrectionType,
              reason: v.reason,
              corrected_gross_kg: v.corrected_gross_kg ?? null,
              corrected_tare_kg: v.corrected_tare_kg ?? null,
              corrected_moisture_pct: v.corrected_moisture_pct ?? null,
              corrected_rate_per_kg: v.corrected_rate_per_kg ?? null,
              corrected_freight_charge: v.corrected_freight_charge ?? null,
              corrected_other_charges: v.corrected_other_charges ?? null,
            })
          }}
        >
          <Form.Item
            name="correction_type"
            label="What's being corrected"
            rules={[{ required: true }]}
          >
            <Select options={[
              { value: 'GROSS', label: 'Gross weight only' },
              { value: 'TARE', label: 'Tare weight only' },
              { value: 'BOTH', label: 'Both gross and tare' },
              { value: 'RECON_FIELDS', label: 'Moisture / rate / freight / other charges' },
              { value: 'MULTIPLE', label: 'Multiple fields' },
            ]} />
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="corrected_gross_kg" label="Corrected gross (kg)">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="corrected_tare_kg" label="Corrected tare (kg)">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="corrected_moisture_pct" label="Corrected moisture %">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="corrected_rate_per_kg" label="Corrected ₹/kg">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="corrected_freight_charge" label="Corrected freight ₹">
                <InputNumber min={0} step={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="corrected_other_charges" label="Corrected other ₹">
                <InputNumber min={0} step={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="reason" label="Reason"
            rules={[{ required: true, message: 'Reason is mandatory' },
                    { min: 5, message: 'Be specific — at least 5 characters' }]}
          >
            <Input.TextArea rows={3}
              placeholder="e.g. Gross showed 12345 but display photo shows 12435 — typo on entry." />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
